/**
 * "Screenshot this page" with the browser's own screen capture
 * (getDisplayMedia): the user picks what to share — this tab is offered first
 * — and one frame is taken, then the stream is stopped. Nothing is captured
 * without that browser prompt, and the caller shows the image for review
 * (patient identifiers) before it is attached. No DOM-to-image library.
 */
export async function captureScreenshot(): Promise<File> {
  if (!navigator.mediaDevices?.getDisplayMedia) throw new Error('This browser cannot take screenshots. Attach an image instead.')
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    audio: false,
    // Chrome: offer the current tab first.
    preferCurrentTab: true,
  } as DisplayMediaStreamOptions & { preferCurrentTab?: boolean })
  try {
    const video = document.createElement('video')
    video.srcObject = stream
    video.muted = true
    await video.play()
    // One more frame so the first painted image is not blank.
    await new Promise((r) => requestAnimationFrame(() => r(null)))
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'))
    if (!blob) throw new Error('The screenshot could not be taken.')
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    return new File([blob], `screenshot-${stamp}.png`, { type: 'image/png' })
  } finally {
    for (const track of stream.getTracks()) track.stop()
  }
}
